import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular/standalone';

import { Capability } from '../../../../common/enums/capability.enum';
import { ProFeature } from '../../../../common/enums/pro-feature-message.enum';
import { PublishMessage } from '../../../../common/enums/publish.enum';
import { ToastLife } from '../../../../common/enums/toast-life.enum';
import { ProGateService } from '../../../core/payment/services/pro-gate.service';
import { SubscriptionService } from '../../../core/payment/services/subscription.service';
import { NetworkService } from '../../../../common/services/network/network.service';
import {
  PublishModalComponent,
  PublishResult,
} from '../components/publish-modal/publish-modal.component';
import { mediaRefsOf } from '../sync/backup-fit.util';
import { SyncService } from '../sync/sync.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { ItemType } from '../models/item-type';
import { DbService } from './db.service';
import { Listing, ListingItemType, PublishingApiService } from './publishing-api.service';
import { PublishingAvailabilityService } from './publishing-availability.service';

/**
 * The one entry point for "Publish to Discover", shared by the sequence and
 * program detail pages.
 *
 * `available` decides whether a publish control is DRAWN at all; the gates below
 * decide whether a drawn one may proceed. Keeping the two apart is the point: a
 * button that is going to be refused should never have been offered. Publishing
 * is off in every deployed environment until it launches, and while it is off
 * every marketplace route answers 404 — so without this the app shows a publish
 * button, opens the sheet, and dies on an error the user can do nothing about.
 *
 * Then three gates run before the sheet opens. Publishing needs the LISTINGS
 * capability, which an admin grants per plan rather than every plan inheriting
 * it — without it the paywall opens instead. And a foreign item is refused here
 * rather than after the sheet is filled in: the server refuses it too, but the
 * app already knows who created it, so asking first wastes nobody's time.
 *
 * Everything else the server checks (cover image, media actually backed up) is
 * reported by the sheet from the server's own message, because those answers
 * depend on state the app cannot see (what the cloud already holds).
 */
/** What in a tree was made by someone else: nothing, an item, or a file. */
type ForeignContent = 'none' | 'items' | 'media';

@Injectable({ providedIn: 'root' })
export class PublishGateService {
  private readonly proGate = inject(ProGateService);
  private readonly subscription = inject(SubscriptionService);
  private readonly modalCtrl = inject(ModalController);
  private readonly toastCtrl = inject(ToastController);
  private readonly alertCtrl = inject(AlertController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly sync = inject(SyncService);
  private readonly network = inject(NetworkService);
  private readonly availability = inject(PublishingAvailabilityService);
  private readonly api = inject(PublishingApiService);
  private readonly db = inject(DbService);
  private readonly auth = inject(AuthenticationService);
  private readonly logger = inject(LogFactory).createLogger(PublishGateService);

  /** The capabilities the current plan holds, as a signal so hidden publish UI
   *  comes back the moment an upgrade lands (no reload). */
  private readonly capabilities = toSignal(this.subscription.capabilities$, {
    initialValue: [] as string[],
  });

  /**
   * Whether to show a publish control at all. Templates bind to this rather than
   * calling the api, so a deployment with publishing switched off simply has no
   * publish affordance — no button, no menu row, no explaining.
   *
   * The plan's LISTINGS capability counts the same way. Publishing is not a
   * feature every plan buys, and offering it to a plan that cannot use it means
   * every route out of the button is a paywall — the "My listings" page included,
   * which would open empty and refuse everything. Hiding is the honest answer;
   * the paywall still belongs on the surfaces a user can reach another way.
   * Absent capabilities (signed out, or entitlement not fetched yet) read as
   * "no" so the control is never drawn and then retracted.
   */
  readonly available = computed(
    () =>
      this.availability.publishingEnabled() &&
      this.capabilities().includes(Capability.LISTINGS),
  );

  /** True when this kind of item can be published at all. */
  canPublishKind(kind: ItemType): boolean {
    return PublishingApiService.listingTypeFor(kind) !== null;
  }

  /** Whether a publish control for this item kind should be drawn. */
  canOfferPublish(kind: ItemType): boolean {
    return this.available() && this.canPublishKind(kind);
  }

  /**
   * Open the publish sheet for a library item. Resolves with what happened, or
   * null when a gate refused or the publisher backed out.
   */
  async open(item: {
    kind: ItemType;
    id: string;
    name?: string;
    createdByUserId?: string;
  }): Promise<PublishResult | null> {
    const itemType = PublishingApiService.listingTypeFor(item.kind);
    if (!itemType) return null;

    // Backstop for the switch flipping while a page is already open — the
    // control is hidden by `available`, so reaching here means it went off
    // under the user's feet, not that they were offered something bogus.
    if (!this.available()) {
      await this.toast(PublishMessage.Unavailable);
      return null;
    }
    // Someone ELSE's id. A bare truthiness test refused an item the user had
    // re-imported from their own .fitpack, which import-review deliberately
    // stamps with their own id and leaves unlocked so it stays theirs.
    if (item.createdByUserId && item.createdByUserId !== this.auth.getUserId()) {
      await this.toast(PublishMessage.BlockedForeign);
      return null;
    }
    // The item being the user's own is not enough: a sequence or program
    // assembled from downloaded community groups/activities would republish
    // their creator's work. The server walks the same tree and refuses too;
    // checking here spares the user filling in a sheet that cannot succeed.
    const foreign = await this.containsForeignContent(item.kind, item.id);
    if (foreign !== 'none') {
      await this.toast(
        foreign === 'media'
          ? PublishMessage.BlockedForeignMedia
          : PublishMessage.BlockedForeignContent,
      );
      return null;
    }
    if (!this.network.online()) {
      await this.toast(PublishMessage.UnavailableOffline);
      return null;
    }
    if (!this.proGate.requireCapability(Capability.LISTINGS, ProFeature.Publishing)) {
      return null;
    }

    // An item carries at most one listing, so reopening the sheet for one that
    // already has a listing has to EDIT that listing. Creating a second is
    // refused by the server, and prefilling from the item would quietly
    // replace what the publisher wrote with the item's own name again. A
    // draft, a rejection and a retirement all reopen as editable — those are
    // the states a publisher returns from. A listing that is live, in review
    // or paused gets the UPDATE offer instead: the publisher tapping Publish
    // on an edited item wants their changes out, and the way changes reach
    // readers is a backup — which they should not have to know.
    const existing = await this.existingListingFor(itemType, item.id);
    if (existing && !this.canReopen(existing.status)) {
      await this.offerListingUpdate();
      return null;
    }

    const modal = await this.modalCtrl.create({
      component: PublishModalComponent,
      componentProps: {
        itemType,
        itemId: item.id,
        itemName: item.name ?? '',
        listingId: existing?.id,
        draftTitle: existing?.title,
        draftDescription: existing?.description ?? undefined,
      },
    });
    await modal.present();
    const { data, role } = await modal.onDidDismiss<PublishResult>();
    if (role !== 'confirm' || !data) return null;

    await this.toast(data.asDraft ? PublishMessage.DraftSaved : PublishMessage.Published);
    return data;
  }

  /** The listing states a publisher can pick back up and edit or resubmit. */
  private canReopen(status: Listing['status']): boolean {
    return status === 'DRAFT' || status === 'REJECTED' || status === 'RETIRED';
  }

  /**
   * What in this item's tree was made by someone else — nothing, an item, or a
   * file. Same discriminator throughout: a creator stamp that is not the
   * signed-in user. Catalog content carries no stamp and never blocks.
   *
   * The two answers are kept apart because they need different words. "Includes
   * items created by someone else" reads as simply wrong to a user who built
   * every item in the tree themselves and only reused a video.
   */
  private async containsForeignContent(kind: ItemType, id: string): Promise<ForeignContent> {
    const myId = this.auth.getUserId();
    const foreign = (it?: { createdByUserId?: string }): boolean =>
      !!it?.createdByUserId && it.createdByUserId !== myId;

    const sequenceIds = new Set<string>();
    const groupIds = new Set<string>();
    const activityIds = new Set<string>();
    // Gathered as the walk goes, and asked about once at the end. Media is what
    // survives when somebody rebuilds around it: a brand-new activity holding
    // another creator's video carries its builder's own stamp, so the rows above
    // have nothing to object to (ADR-0260).
    const mediaIds: Array<string | undefined> = [];
    const visit = (row: unknown): boolean => {
      // A membership row can name an item that is no longer here. It holds no
      // media and is nobody's work — the same answer the walk gave before.
      if (!row) return false;
      mediaIds.push(...mediaRefsOf(row));
      return foreign(row as { createdByUserId?: string });
    };

    if (kind === ItemType.Program) {
      if (visit(await this.db.getProgram(id))) return 'items';
      for (const it of await this.db.getProgramItems(id)) {
        if (it.sequenceId) sequenceIds.add(it.sequenceId);
        if (it.groupId) groupIds.add(it.groupId);
        if (it.activityId) activityIds.add(it.activityId);
      }
      for (const sid of sequenceIds) {
        if (visit(await this.db.getSequence(sid))) return 'items';
      }
    } else {
      if (visit(await this.db.getSequence(id))) return 'items';
      sequenceIds.add(id);
    }

    for (const sid of sequenceIds) {
      for (const it of await this.db.getSequenceItems(sid)) {
        if (it.groupId) groupIds.add(it.groupId);
        if (it.activityId) activityIds.add(it.activityId);
      }
    }
    for (const gid of groupIds) {
      if (visit(await this.db.getGroup(gid))) return 'items';
      for (const a of await this.db.getActivitiesByGroup(gid)) if (visit(a)) return 'items';
    }
    for (const aid of activityIds) {
      if (visit(await this.db.getActivity(aid))) return 'items';
    }

    const foreignMedia = await this.db.foreignMediaIds(mediaIds, myId);
    return foreignMedia.size ? 'media' : 'none';
  }

  /**
   * Publish tapped on an item that is already listed: offer to back it up,
   * because a backup IS how edits reach the listing — the server reads the
   * published content live from the publisher's cloud, and a change to a live
   * listing drops it back into review on its own. Without this offer the tap
   * just said "already listed", and a publisher who had edited their item was
   * left wondering why they could not re-publish.
   *
   * Backing up needs its own capability (a plan can grant publishing without
   * cloud sync), so the paywall is checked before anything is offered.
   */
  private async offerListingUpdate(): Promise<void> {
    if (!this.proGate.requireCapability(Capability.CLOUD_SYNC, ProFeature.CloudSync)) {
      return;
    }

    const alert = await this.alertCtrl.create({
      header: PublishMessage.UpdateHeader,
      message: PublishMessage.UpdateMessage,
      buttons: [
        { text: PublishMessage.BackupNeededCancel, role: 'cancel' },
        { text: PublishMessage.UpdateConfirm, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') return;

    // A backup of a big library takes a while; a silent wait reads as a dead
    // button, so say what is happening until it finishes.
    const loading = await this.loadingCtrl.create({ message: PublishMessage.BackingUp });
    await loading.present();
    try {
      await this.sync.syncNow();
      await this.toast(PublishMessage.UpdateSent);
    } catch (err) {
      this.logger.error('Backup for a listing update failed', err);
      await this.toast(PublishMessage.BackupFailed);
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Submit — or resubmit — a listing for review. Same sheet, same agreement:
   * a draft was never validated or consented to, and a rejected or retired
   * listing needs a fresh pass and fresh consent, so every submission runs
   * the full gate. It patches the existing listing rather than creating a
   * second one, because an item can only carry one.
   */
  async submitDraft(listing: {
    id: string;
    itemType: ListingItemType;
    itemId: string;
    title?: string;
    description?: string | null;
  }): Promise<PublishResult | null> {
    if (!this.available()) {
      await this.toast(PublishMessage.Unavailable);
      return null;
    }
    // The same two refusals as a first publish. A draft can be saved and
    // resubmitted days later, over content that has changed in between — and
    // this path used to run neither check, leaving the server as the only thing
    // standing between a resubmission and somebody else's work.
    const foreign = await this.containsForeignContent(
      listing.itemType === 'PROGRAM' ? ItemType.Program : ItemType.Sequence,
      listing.itemId,
    );
    if (foreign !== 'none') {
      await this.toast(
        foreign === 'media'
          ? PublishMessage.BlockedForeignMedia
          : PublishMessage.BlockedForeignContent,
      );
      return null;
    }
    if (!this.network.online()) {
      await this.toast(PublishMessage.UnavailableOffline);
      return null;
    }
    if (!this.proGate.requireCapability(Capability.LISTINGS, ProFeature.Publishing)) {
      return null;
    }

    const modal = await this.modalCtrl.create({
      component: PublishModalComponent,
      componentProps: {
        itemType: listing.itemType,
        itemId: listing.itemId,
        itemName: listing.title ?? '',
        listingId: listing.id,
        // Prefill what the listing already says, so a resubmission edits the
        // publisher's words instead of silently reverting them.
        draftTitle: listing.title,
        draftDescription: listing.description ?? undefined,
      },
    });
    await modal.present();
    const { data, role } = await modal.onDidDismiss<PublishResult>();
    if (role !== 'confirm' || !data) return null;

    await this.toast(PublishMessage.Published);
    return data;
  }

  /**
   * The caller's existing listing for this item, if there is one.
   *
   * Best-effort: a publisher who has never published has no listings, and a
   * lookup that fails should not stop them publishing. Falling back to "none"
   * means the worst case is the server's own conflict answer, not a dead button.
   */
  private async existingListingFor(
    itemType: ListingItemType,
    itemId: string,
  ): Promise<Listing | null> {
    try {
      const mine = await this.api.listMine();
      return mine.find((l) => l.itemType === itemType && l.itemId === itemId) ?? null;
    } catch (err) {
      this.logger.error('Could not check for an existing listing', err, { itemId });
      return null;
    }
  }

  private async toast(message: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: ToastLife.MEDIUM,
      position: 'bottom',
    });
    await toast.present();
  }
}
